import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganizationDependencyContainer } from '../../infrastructure/dependencies/OrganizationDependencyContainer';
import { Organization } from '../../domain/entities/Organization';
import { MyOrganizationUser, OrgConnection } from '../../domain/repositories/IOrganizationRepository';
import { useMyOrganizationStore } from '../store/myOrganizationStore';
import { useAuthStore } from '@/auth/presentation/store/authStore';

/**
 * Fachada de `/v2/organizations/me`: un `useState` + `useEffect` POR INSTANCIA, es decir, una copia
 * aislada del dato por cada componente que lo llama (hoy 8). `reload()` refresca solo la copia de
 * quien lo invoca; para refrescar TODAS —el caso de una mutación que cambia el estado de la
 * organización y debe verse en el layout persistente, no solo en la página que la disparó— hay que
 * bumpear la señal compartida `useMyOrganizationStore.invalidate()`, que este hook observa.
 */
export function useMyOrganization(_slug: string | undefined) {
  const { t } = useTranslation();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [users, setUsers] = useState<MyOrganizationUser[]>([]);
  const [connections, setConnections] = useState<OrgConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const role = useAuthStore((s) => s.user?.role);
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const version = useMyOrganizationStore((s) => s.version);

  const load = useCallback(async () => {
    // Sin sesión (p.ej. /pricing en modo anónimo): no pedir /organizations/me — daría 401
    // y dispararía el "Forzando logout" en un usuario que nunca estuvo logado.
    if (!isLoggedIn) return;
    if (role === 'superadmin') return;

    setIsLoading(true);
    setError(null);
    try {
      const orgRepo = OrganizationDependencyContainer.getOrganizationRepository();
      const result = await orgRepo.getMe();
      setOrganization(result.organization);
      setUsers(result.users);
      setConnections(result.connections);
    } catch {
      // Fallo de carga de página: el detalle crudo del backend (código o "Request failed with
      // status code N" de axios) no aporta nada al usuario y quedaba visible sin traducir. Se
      // muestra un mensaje genérico traducido; la página ofrece reintento vía reload().
      setError(t('common.load_error'));
    } finally {
      setIsLoading(false);
    }
    // `t` se omite a propósito: react-i18next devuelve una identidad nueva por render e incluirla
    // recrearía `load` en bucle con el useEffect. Se cierra sobre la `t` del render actual, que
    // traduce igual de bien en el instante del error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isLoggedIn]);

  // `version` va en las dependencias del efecto, no en las de `load`: cada bump de la señal
  // compartida vuelve a ejecutarlo. Es la excepción que la propia regla exhaustive-deps contempla
  // para efectos (dependencia extra deliberada, no usada en el cuerpo). Mantenerla fuera de `load`
  // preserva la identidad de `reload` entre bumps, que varios consumidores pasan como callback.
  useEffect(() => {
    load();
  }, [load, version]);

  return { organization, users, connections, isLoading, error, reload: load };
}
